import React, { useRef, useState } from "react";
import TopoTexture from "@/components/TopoTexture";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_premium-moving-wy/artifacts/vqqb3ymd_CTM_Gerneral.mp4";

const VideoSection = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      data-testid="video-section"
      className="relative py-20 md:py-28 bg-[var(--ct-primary)] text-white overflow-hidden"
    >
      <TopoTexture variant="dark" opacity={0.05} />
      {/* Decorative yellow stripe */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--ct-secondary)]" />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[var(--ct-secondary)]/10 blur-3xl pointer-events-none" />

      <div className="section-container relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-4 order-2 lg:order-1"
          >
            <div className="label-xs text-white mb-3">See Us in Action</div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]"
              style={{ fontFamily: "Outfit" }}
            >
              The Crosstown Movers difference,{" "}
              <span className="text-[var(--ct-secondary)] whitespace-nowrap">on every move.</span>
            </h2>
            <p className="mt-5 text-white/80 leading-relaxed">
              Real Wyoming crews. Real care. Watch how our team protects, packs, and
              transports homes and businesses across Casper, Mills, and the surrounding
              areas — the same way we'll handle yours.
            </p>
            <a href="#quote" className="btn-primary mt-7" data-testid="video-cta">
              Get Your Free Quote
            </a>
          </motion.div>

          {/* Video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 order-1 lg:order-2"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.4)] border border-white/10 group">
              <video
                ref={ref}
                src={VIDEO_URL}
                data-testid="brand-video"
                className="w-full aspect-video object-cover bg-black"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              />

              {/* Custom controls overlay */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center gap-2">
                  <button
                    onClick={togglePlay}
                    aria-label={playing ? "Pause" : "Play"}
                    data-testid="video-play"
                    className="w-11 h-11 rounded-full bg-white/95 text-[var(--ct-primary)] flex items-center justify-center hover:bg-[var(--ct-secondary)] transition"
                  >
                    {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </button>
                  <button
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute" : "Mute"}
                    data-testid="video-mute"
                    className="w-11 h-11 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition"
                  >
                    {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                </div>
                <div className="px-3 py-1.5 rounded-full bg-black/40 backdrop-blur text-xs font-semibold tracking-widest uppercase text-white">
                  Crosstown Movers · Wyoming
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
